import React from 'react';
import { Layout, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
  EditOutlined,
  BarChartOutlined,
  UserOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  ReadOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';

const { Sider } = Layout;

/**
 * Sidebar 左侧导航栏组件
 * 变更说明：
 * 1) 将“人员统计”移动到最高一级菜单，位置在“工作日志”下方；
 * 2) 所有用户均可见（加入基础菜单 baseItems）；
 * 3) 从“数据分析”子菜单中移除“人员统计”，避免重复显示。
 */
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  // 根据用户角色显示不同的菜单项
  const getMenuItems = () => {
    const baseItems = [
      {
        key: '/',
        icon: <DashboardOutlined />,
        label: '工作台'
      },
      {
        key: '/projects',
        icon: <ProjectOutlined />,
        label: '项目中心'
      },
      {
        key: '/my-tasks',
        icon: <CheckSquareOutlined />,
        label: '我的任务'
      },
      {
        key: '/daily-log',
        icon: <EditOutlined />,
        label: '工作日志'
      },
      {
        key: '/art-production',
        icon: <ToolOutlined />,
        label: '美工制作'
      },
      // 新增：学习论坛（所有用户可见）
      {
        key: 'forum',
        icon: <ReadOutlined />,
        label: '学习论坛',
        children: [
          { key: '/forum/ai-design', label: 'AI设计' },
          { key: '/forum/new-material-process', label: '新材料工艺' },
          { key: '/forum/exhibition-checkin', label: '展馆打卡' }
        ]
      },
      // 人员统计：移动到最高一级，所有用户可见
      {
        key: '/analytics/personal-stats',
        icon: <BarChartOutlined />,
        label: '任务统计'
      },
      // 新增：项目分析 顶级菜单项（设计负责人查看我负责项目的工作量统计）
      {
        key: '/analytics/project-analysis',
        icon: <ProjectOutlined />,
        label: '项目分析'
      },
      // 加班申请：所有用户可见
      {
        key: '/overtime',
        icon: <ClockCircleOutlined />,
        label: '加班申请'
      },
      // 新增：申请调休 顶级菜单项
      {
        key: '/compensatory',
        icon: <ClockCircleOutlined />,
        label: '申请调休'
      }
      // 注意：移除原先的"加班调休统计"顶级菜单项，改放入"数据分析"子菜单
    ];

    // 管理员和主管可以看到的菜单
    const managerItems = [
      {
        key: '/tasks',
        icon: <CheckSquareOutlined />,
        label: '任务管理'
      },
      {
        key: 'analytics',
        icon: <BarChartOutlined />,
        label: '数据分析',
        children: [
          {
            key: '/analytics/team-workload',
            label: '团队负荷'
          },
          {
            key: '/analytics/project-progress',
            label: '项目进度'
          },
          {
            key: '/analytics/project-logs',
            label: '项目日志'
          },
          // 已移除“人员统计”，避免重复
          {
            key: '/analytics/profit-loss',
            label: '盈亏统计'
          },
          // 新增：日志统计
          {
            key: '/analytics/log-stats',
            label: '日志统计'
          },
          // 新增：加班调休统计（仅管理员与设计部经理在“数据分析”下可见）
          {
            key: '/analytics/overtime-comp',
            label: '加班调休统计'
          }
        ]
      }
    ];

    // 设计主管专用的系统管理菜单
    const managerAdminItems = [
      {
        key: 'manager-admin',
        icon: <SettingOutlined />,
        label: '系统管理',
        children: [
          {
            key: '/admin/templates',
            icon: <SettingOutlined />,
            label: '常用任务'
          },
          {
            key: '/admin/overtime',
            icon: <CheckSquareOutlined />,
            label: '加班审批'
          },
          {
            key: '/admin/compensatory',
            icon: <CheckSquareOutlined />,
            label: '调休审批'
          }
        ]
      }
    ];

    // 管理员专用菜单
    const adminItems = [
      {
        key: 'admin',
        icon: <SettingOutlined />,
        label: '系统管理',
        children: [
          {
            key: '/admin/users',
            icon: <UserOutlined />,
            label: '用户管理'
          },
          {
            key: '/admin/templates',
            icon: <SettingOutlined />,
            label: '常用任务'
          },
          {
            key: '/admin/system-parameters',
            icon: <SettingOutlined />,
            label: '系统参数设置'
          },
          {
            key: '/admin/overtime',
            icon: <CheckSquareOutlined />,
            label: '加班审批'
          },
          {
            key: '/admin/compensatory',
            icon: <CheckSquareOutlined />,
            label: '调休审批'
          }
        ]
      }
    ];

    // 项目经理可以看到任务管理
    const pmItems = [
      {
        key: '/tasks',
        icon: <CheckSquareOutlined />,
        label: '任务管理'
      }
    ];

    if (user?.role === 'admin') {
      return [...baseItems, ...managerItems, ...adminItems];
    } else if (user?.role === 'manager') {
      return [...baseItems, ...managerItems, ...managerAdminItems];
    } else if (user?.role === 'pm') {
      return [...baseItems, ...pmItems];
    } else {
      return baseItems;
    }
  };

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  return (
    <Sider 
      width={240}
      style={{
        background: '#fff',
        borderRight: '1px solid #f0f0f0'
      }}
    >
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={getMenuItems()}
        onClick={handleMenuClick}
        style={{
          border: 'none',
          height: '100%'
        }}
      />
    </Sider>
  );
};

export default Sidebar;
